import type { ToolCallCardProps, ToolCallData } from '../types';
import type { JsonValue } from '@zenmako/contracts/common';
import type { VNode } from 'preact';
import { ToolResultRenderer } from './ToolResultRenderer';
import { useState } from 'preact/hooks';
import { toast } from '../../../services/toast.service';
import { authService } from '../../../services/auth/authService';

interface Connection {
  connectionId: string;
  connectorId: string;
  active: boolean;
}

interface ConnectionsResponse {
  connections: Connection[];
}

interface ImportMetaEnv {
  VITE_API_URL: string;
}

interface ImportMeta {
  env: ImportMetaEnv;
}

/**
 * Return the API URL from Vite environment.
 * Centralized to avoid repeating the cast everywhere.
 */
function getApiUrl(): string {
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  return (import.meta as unknown as ImportMeta).env.VITE_API_URL;
}

interface UISpecButton {
  label: string;
  action: string;
  style: string;
}

interface ExecuteResponse {
  success?: boolean;
  message?: string;
  data?: JsonValue;
}

/**

/**
 * Get fallback icon based on tool name (used when no connector icon is available)
 */
function getFallbackIcon(_toolName: string): string {
  return '⚡';
}

/**
 * Get display title based on tool name
 * Converts camelCase tool names to readable titles
 * e.g. "searchEmailsByCriteria" → "Search Emails By Criteria"
 *      "core_markTodoItemComplete" → "Mark Todo Item Complete"
 */
function getToolTitle(toolName: string | undefined): string {
  if (!toolName) return 'Unknown Tool';
  // Extract the action part (after connector prefix)
  const parts = toolName.split('_');
  const actionPart = parts.length >= 2 ? parts.slice(1).join('_') : toolName;
  // Convert camelCase to spaced words and capitalize
  return actionPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp: Date | string): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleTimeString(undefined, { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
}

/**
 * Individual tool call card component
 */
export function ToolCallCard({ toolCall, className = '', showButtons = false, onToolUpdate, threadId, teamId, messageId, connectorIcon, connectorName }: ToolCallCardProps): VNode<Record<string, never>> {
  console.log('🔍 [ToolCallCard] Received toolCall:', {
    toolName: toolCall.toolName,
    hasOutput: !!toolCall.output,
    outputKeys: toolCall.output ? Object.keys(toolCall.output) : [],
    output: toolCall.output,
    outputType: typeof toolCall.output,
    showButtons
  });

  // Backend always unwraps AI SDK's {type, value} format before storing
  const outputValue = toolCall.output;

  // Start expanded if requires approval (so buttons are immediately visible)
  const [isExpanded, setIsExpanded] = useState(showButtons && Boolean(outputValue?.requiresApproval));
  const [isExecuting, setIsExecuting] = useState(false);
  const [isHandled, setIsHandled] = useState(false); // Track if tool has been approved/cancelled
  const fallbackIcon = getFallbackIcon(toolCall.toolName);
  const title = getToolTitle(toolCall.toolName);

  // Extract description from output
  const description = outputValue?.message || 'No description available';

  const time = formatTime(toolCall.timestamp);
  const hasResults = Boolean(outputValue?.permission && outputValue.data);
  const hasApprovalButtns = showButtons && Boolean(outputValue?.requiresApproval) && !isHandled;
  const hasExpandableContent = true; // Always allow expanding to show args

  // Debug logging for approval detection
  console.log('ToolCallCard Debug:', {
    toolName: toolCall.toolName,
    showButtons,
    requiresApproval: !!outputValue?.requiresApproval,
    hasPermission: !!outputValue?.permission,
    hasApprovalSection: !!outputValue?.permission?.approval,
    hasApprovalButtons: !!outputValue?.permission?.approval?.buttons,
    hasResults,
    hasApproval: hasApprovalButtns,
    hasExpandableContent,
    isExpanded,
    permissionApproval: outputValue?.permission?.approval,
    outputValue
  });

  // Get authentication headers
  const getHeaders = async (): Promise<HeadersInit> => {
    const token = await authService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Handle approval action (approve/cancel)
  const handleApprovalAction = async (apiUrl: string, action: string, toolCall: ToolCallData, threadId: string): Promise<void> => {
    // Prevent multiple simultaneous executions
    if (isExecuting) {
      console.log('Already executing, ignoring click');
      return;
    }

    console.log('Approval action clicked:', { action, toolCall });
    // Backend always unwraps AI SDK's {type, value} format before storing
    const outputValue = toolCall.output;

    if (action === 'cancel') {
      setIsExecuting(true);
      setIsHandled(true); // Mark as handled to prevent buttons from reappearing
      try {
        // Extract connectorId and toolId from the output
        const connectorId = outputValue?.connectorId;
        const toolId = outputValue?.toolId;

        console.log('DEBUG: Cancel button clicked with data:', {
          connectorId,
          toolId,
          outputValue,
          apiUrl
        });

        if (!connectorId || !toolId) {
          console.error('Missing connectorId or toolId for cancellation:', { connectorId, toolId, outputValue });
          // Fall back to local cancellation if no endpoint info available
        } else {
          // Call the cancel endpoint to update database
          try {
            // Ensure we have teamId for external connectors
            if (!teamId) {
              throw new Error('Team ID is required');
            }

            const definedTeamId: string = teamId;
            const definedConnectorId: string = connectorId;
            const definedToolId: string = toolId;
            let connectionId: string;

            // Core connector doesn't need connection lookup (built-in connector)
            if (connectorId === 'core') {
              connectionId = 'core';
              console.log('Using synthetic connectionId for core connector cancellation');
            } else {
              // External connectors: look up active connection from database
              const connectionsResponse = await fetch(`${apiUrl}/teams/${definedTeamId}/connections?active=true&connectorId=${connectorId}`, {
                headers: await getHeaders()
              });

              if (!connectionsResponse.ok) {
                throw new Error('Failed to get connections');
              }

              const connectionsData = (await connectionsResponse.json()) as ConnectionsResponse;
              const activeConnections = connectionsData.connections.filter((conn: Connection) =>
                conn.connectorId === connectorId && conn.active
              );

              if (activeConnections.length === 0) {
                throw new Error('No active connection found');
              }

              connectionId = activeConnections[0].connectionId;
            }

            // Call the cancel endpoint (connectorId and toolId are checked to be defined above)
            const cancelResponse = await fetch(`${apiUrl}/teams/${definedTeamId}/connections/${connectionId}/cancel/${definedConnectorId}_${definedToolId}`, {
              method: 'POST',
              headers: await getHeaders(),
              body: JSON.stringify({
                toolCallId: toolCall.id,
                threadId: threadId,
                messageId: messageId
              })
            });

            if (cancelResponse.ok) {
              console.log('Tool cancellation successfully sent to backend');
            } else {
              console.warn('Cancel endpoint failed, proceeding with local cancellation');
            }
          } catch (endpointError) {
            console.warn('Error calling cancel endpoint, proceeding with local cancellation:', endpointError);
          }
        }

        // Update the tool call output to show it was cancelled (local update, use unwrapped format)
        const cancelledToolCall = {
          ...toolCall,
          output: {
            ...outputValue,
            success: false,
            requiresApproval: false,
            message: 'Tool execution cancelled by user',
            data: null,
            permission: outputValue?.permission ? {
              ...outputValue.permission,
              approval: undefined // Remove approval section to hide buttons
            } : undefined
          }
        };

        // Notify parent component of the update
        if (onToolUpdate) {
          onToolUpdate(cancelledToolCall);
        }

        console.log('Tool execution cancelled by user:', toolCall.id);

      } catch (error) {
        console.error('Error cancelling tool:', error);
        toast.error('Failed to cancel tool');
      } finally {
        setIsExecuting(false);
      }
      return;
    }
    
    if (action === 'approve') {
      setIsExecuting(true);
      setIsHandled(true); // Mark as handled to prevent buttons from reappearing
      try {
        // Extract connectorId and toolId from the output
        const connectorId = outputValue?.connectorId;
        const toolId = outputValue?.toolId;

        if (!connectorId || !toolId) {
          console.error('Missing connectorId or toolId for execution:', { connectorId, toolId, outputValue });
          toast.error('Missing connection information');
          setIsHandled(false);
          return;
        }
        
        // Get connectionId for execution
        console.log('Resolving connectionId for execution...', { connectorId, toolId });

        // Ensure we have teamId for external connectors
        if (!teamId) {
          console.error('No teamId available for connections lookup');
          toast.error('Team ID is required');
          setIsHandled(false);
          return;
        }

        const definedTeamId: string = teamId;
        const definedConnectorId: string = connectorId;
        const definedToolId: string = toolId;
        let connectionId: string;

        // Core connector doesn't need connection lookup (built-in connector)
        if (connectorId === 'core') {
          connectionId = 'core';
          console.log('Using synthetic connectionId for core connector');
        } else {
          // External connectors: look up active connection from database
          // Call connections API to find active connection for this connector
          const connectionsResponse = await fetch(`${apiUrl}/teams/${definedTeamId}/connections?active=true&connectorId=${connectorId}`, {
            headers: await getHeaders()
          });

          if (!connectionsResponse.ok) {
            console.error('Failed to get connections:', connectionsResponse.status);
            toast.error('Could not find connection information');
            setIsHandled(false);
            return;
          }

          const connectionsData = (await connectionsResponse.json()) as ConnectionsResponse;
          const activeConnections = connectionsData.connections.filter((conn: Connection) =>
            conn.connectorId === connectorId && conn.active
          );

          if (activeConnections.length === 0) {
            console.error('No active connections found for connector:', connectorId);
            toast.error(`No active connection found for ${connectorId}`);
            setIsHandled(false);
            return;
          }

          // Use the first active connection (in the future, might need user selection for multiple)
          connectionId = activeConnections[0].connectionId;
          console.log('Found connectionId:', connectionId);
        }

        // Now execute the tool (connectorId and toolId are checked to be defined above)
        console.log('🚨 APPROVE BUTTON CLICKED - Debug Info:', {
          messageId,
          hasMessageId: !!messageId,
          messageIdType: typeof messageId,
          threadId,
          toolCallId: toolCall.id,
          payload: {
            args: toolCall.args,
            toolCallId: toolCall.id,
            threadId,
            messageId,
            connectorId,
            toolId,
            connectionId
          }
        });

        const executeResponse = await fetch(`${apiUrl}/teams/${definedTeamId}/connections/${connectionId}/execute/${definedConnectorId}_${definedToolId}`, {
          method: 'POST',
          headers: await getHeaders(),
          body: JSON.stringify({
            args: toolCall.args,
            toolCallId: toolCall.id,
            threadId,
            messageId,
            connectorId,
            toolId,
            connectionId
          })
        });
        
        if (!executeResponse.ok) {
          const errorText = await executeResponse.text();
          console.error('Execute request failed:', executeResponse.status, errorText);
          toast.error(`Execution failed: ${errorText}`);
          // Reset handled state on error so user can retry
          setIsHandled(false);
          return;
        }

        const result = (await executeResponse.json()) as ExecuteResponse;
        console.log('Execution result:', result);

        // Update the tool call output to remove approval requirement and show execution result (use unwrapped format)
        const updatedToolCall = {
          ...toolCall,
          output: {
            ...outputValue,
            success: result.success ?? true,
            requiresApproval: false,
            message: result.message !== undefined && result.message !== '' ? result.message : 'Tool executed successfully',
            data: (result.data || result) as JsonValue,
            permission: outputValue?.permission ? {
              ...outputValue.permission,
              approval: undefined // Remove approval section to hide buttons
            } : undefined
          }
        };
        
        // Notify parent component of the update
        if (onToolUpdate) {
          onToolUpdate(updatedToolCall);
        }

        // TODO: Update the database record with the execution result
        toast.success(result.message || 'Tool executed successfully');

      } catch (error) {
        console.error('Error executing approved tool:', error);
        toast.error('Failed to execute tool');
        setIsHandled(false); // Reset on error so user can retry
      } finally {
        setIsExecuting(false);
      }
    }
  };

  /**
   * Render the connector icon (SVG from metadata, or fallback emoji)
   */
  const renderIcon = (): VNode => {
    if (connectorIcon?.content) {
      if (connectorIcon.content.startsWith('<svg')) {
        const sizedSvg = connectorIcon.content.replace(/<svg[^>]*>/, (match: string) =>
          match
            .replace(/width="[^"]*"/, 'width="20"')
            .replace(/height="[^"]*"/, 'height="20"')
        );
        return (
          <div
            className="w-5 h-5 flex-shrink-0 flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: sizedSvg }}
          />
        );
      }
      return <span className="text-lg flex-shrink-0">{connectorIcon.content}</span>;
    }
    if (connectorIcon?.url) {
      return <img src={connectorIcon.url} alt={connectorName || title} className="w-5 h-5 flex-shrink-0 rounded" />;
    }
    return <span className="text-lg flex-shrink-0">{fallbackIcon}</span>;
  };

  // ZM-055: before a tool runs there is no result yet, so the spec used to be
  // rendered against `toolCall.args`. A spec's placeholders are written for the
  // tool's *output*, so on an approval card they resolve to nothing and the user
  // is shown literal `{{to}}` / `{{subject}}` — worst of all on the panel where
  // they approve sending an email, which then fails to say who it is going to.
  //
  // Previews are therefore opt-in per tool. A spec still renders normally once
  // real result data exists; only the pre-execution preview is gated. Tools whose
  // preview has been checked by hand go in the allowlist below, which is expected
  // to grow as each is verified.
  //
  // The fallback is not a blank space: the card falls through to the
  // Parameters table, which shows the same values accurately.
  const PREVIEW_VERIFIED_TOOLS = new Set<string>([
    // Verified rendering correctly against arguments:
    'todo.initializeTodolist',
    'core_initializeTodolist',
  ]);

  const hasResultData = outputValue?.data !== undefined && outputValue?.data !== null;
  // `toolId` carries the connector-prefixed form and `toolName` the display form,
  // so check both rather than assuming which one this call was built with.
  const previewAllowed =
    hasResultData ||
    PREVIEW_VERIFIED_TOOLS.has(toolCall.toolName) ||
    (toolCall.toolId !== undefined && PREVIEW_VERIFIED_TOOLS.has(toolCall.toolId));

  if (outputValue?.componentUISpec && previewAllowed) {
    const componentUISpec = outputValue.componentUISpec;
    const dataToRender = (outputValue.data ?? toolCall.args) as JsonValue;

    return (
      <div className={`${className}`}>
        <div className={`flex items-center gap-3 p-4 bg-base-200 rounded-lg border-l-3 ${outputValue?.success === false ? 'border-l-error' : outputValue?.success === true ? 'border-l-success' : 'border-l-base-300'}`}>
          {renderIcon()}
          <div
            className="flex-1 min-w-0 px-4 cursor-pointer"
            onClick={(): void => { setIsExpanded(!isExpanded); }}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{title}</span>
              {connectorName && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-300 text-base-content/50 font-medium uppercase tracking-wide">{connectorName}</span>
              )}
            </div>
            <div className="text-xs text-base-content/70">{description}</div>
          </div>
          <div className="text-xs text-base-content/50">{time}</div>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
            className="appearance-none bg-transparent hover:bg-base-200 rounded px-2 py-1 text-xs transition-colors"
            aria-label={isExpanded ? 'Collapse results' : 'Expand results'}
          >
            {isExpanded ? '▲' : '▼'}
          </button>
        </div>
        {isExpanded && (
          <div className="px-3 pb-3">
            <ToolResultRenderer
              uiSpec={componentUISpec}
              data={dataToRender}
              className="mt-2"
            />
            {hasApprovalButtns && (
              <div className="flex items-center justify-between p-2 border border-base-200 mt-2">
                <span className="text-sm text-base-content/60">
                  {outputValue.permission?.approval?.message || 'Do you want to proceed?'}
                </span>
                <div className="flex gap-2">
                  <button
                    className={`btn btn-primary ${isExecuting ? 'loading' : ''}`}
                    data-action="approve"
                    onClick={(): void => { void handleApprovalAction(getApiUrl(), 'approve', toolCall, threadId || 'unknown'); }}
                    disabled={isExecuting}
                  >
                    {isExecuting ? 'Executing...' : 'Approve'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    data-action="cancel"
                    onClick={(): void => { void handleApprovalAction(getApiUrl(), 'cancel', toolCall, threadId || 'unknown'); }}
                    disabled={isExecuting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className={`flex items-center gap-3 p-4 bg-base-200 rounded-lg border-l-3 ${outputValue?.success === false ? 'border-l-error' : outputValue?.success === true ? 'border-l-success' : 'border-l-base-300'}`}>
        {renderIcon()}
        <div
          className={`flex-1 min-w-0 px-4 ${hasExpandableContent ? 'cursor-pointer' : ''}`}
          onClick={hasExpandableContent ? (): void => { setIsExpanded(!isExpanded); } : undefined}
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{title}</span>
            {connectorName && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-300 text-base-content/50 font-medium uppercase tracking-wide">{connectorName}</span>
            )}
          </div>
          <div className="text-xs text-base-content/70">{description}</div>
        </div>
        <div className="text-xs text-base-content/50">{time}</div>

        {toolCall.error && (
          <div className="text-xs text-error">
            {toolCall.error}
          </div>
        )}

        {hasExpandableContent && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="appearance-none bg-transparent hover:bg-base-200 rounded px-2 py-1 text-xs transition-colors"
            aria-label={isExpanded ? 'Collapse results' : 'Expand results'}
          >
            {isExpanded ? '▲' : '▼'}
          </button>
        )}
      </div>
      
      {hasExpandableContent && isExpanded && (
        <div className="px-3 pb-3">
          {/* Show args as a clean key-value table */}
          {toolCall.args && Object.keys(toolCall.args).length > 0 && (
            <div className="mt-2 rounded border border-base-300 overflow-hidden text-xs">
              <div className="px-3 py-1.5 bg-base-300/50 font-medium text-base-content/60 uppercase tracking-wide text-[10px]">Parameters</div>
              <div className="divide-y divide-base-300/50">
                {Object.entries(toolCall.args).map(([key, val]) => (
                  <div key={key} className="flex gap-3 px-3 py-1.5">
                    <span className="text-base-content/50 min-w-[80px] font-mono">{key}</span>
                    <span className="text-base-content font-mono break-all">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Show result data as a clean key-value table */}
          {outputValue?.data && typeof outputValue.data === 'object' && !Array.isArray(outputValue.data) && Object.keys(outputValue.data).length > 0 && (
            <div className="mt-2 rounded border border-base-300 overflow-hidden text-xs">
              <div className="px-3 py-1.5 bg-base-300/50 font-medium text-base-content/60 uppercase tracking-wide text-[10px]">Result</div>
              <div className="divide-y divide-base-300/50">
                {Object.entries(outputValue.data as Record<string, unknown>).slice(0, 10).map(([key, val]) => (
                  <div key={key} className="flex gap-3 px-3 py-1.5">
                    <span className="text-base-content/50 min-w-[80px] font-mono">{key}</span>
                    <span className="text-base-content font-mono break-all truncate">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
                {Object.keys(outputValue.data as Record<string, unknown>).length > 10 && (
                  <div className="px-3 py-1.5 text-base-content/40 italic">...and {Object.keys(outputValue.data as Record<string, unknown>).length - 10} more fields</div>
                )}
              </div>
            </div>
          )}
          {/* Show array result data as count + preview */}
          {outputValue?.data && Array.isArray(outputValue.data) && outputValue.data.length > 0 && (
            <div className="mt-2 rounded border border-base-300 overflow-hidden text-xs">
              <div className="px-3 py-1.5 bg-base-300/50 font-medium text-base-content/60 uppercase tracking-wide text-[10px]">Result ({outputValue.data.length} items)</div>
              <div className="divide-y divide-base-300/50">
                {(outputValue.data as unknown[]).slice(0, 5).map((item, idx) => (
                  <div key={idx} className="px-3 py-1.5 font-mono text-base-content break-all truncate">
                    {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                  </div>
                ))}
                {outputValue.data.length > 5 && (
                  <div className="px-3 py-1.5 text-base-content/40 italic">...and {outputValue.data.length - 5} more items</div>
                )}
              </div>
            </div>
          )}
          {(hasResults || hasApprovalButtns) && outputValue?.permission && (
            <ToolResultRenderer
              uiSpec={outputValue.permission}
              // eslint-disable-next-line @typescript-eslint/no-restricted-types
              data={toolCall as unknown as JsonValue}
              className="mt-2"
            />
          )}
          {hasApprovalButtns && (
            <div className="flex items-center justify-between p-2 border border-base-200 mt-2">
              <span className="text-sm text-base-content/60">
                {outputValue?.permission?.approval?.message || 'Do you want to proceed?'}
              </span>
              <div className="flex gap-2">
                {/* Use custom UI spec buttons if available, otherwise show default buttons */}
                {outputValue?.permission?.approval?.buttons && outputValue.permission.approval.buttons.length > 0 ? (
                  outputValue.permission.approval.buttons.map((button: UISpecButton, index: number) => (
                    <button
                      key={index}
                      className={`btn btn-${button.style} ${isExecuting ? 'loading' : ''}`}
                      data-action={button.action}
                      onClick={(): void => {
                        void handleApprovalAction(getApiUrl(), button.action, toolCall, threadId || 'unknown');
                      }}
                      disabled={isExecuting}
                    >
                      {isExecuting && button.action === 'approve' ? 'Executing...' : button.label}
                    </button>
                  ))
                ) : (
                  /* Default approval buttons when no UI spec provided */
                  <>
                  <>
                    <button
                      className={`btn btn-primary ${isExecuting ? 'loading' : ''}`}
                      data-action="approve"
                      onClick={(): void => {
                        void handleApprovalAction(getApiUrl(), 'approve', toolCall, threadId || 'unknown');
                      }}
                      disabled={isExecuting}
                    >
                      {isExecuting ? 'Executing...' : 'Approve'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      data-action="cancel"
                      onClick={(): void => {
                        void handleApprovalAction(getApiUrl(), 'cancel', toolCall, threadId || 'unknown');
                      }}
                      disabled={isExecuting}
                    >
                      Cancel
                    </button>
                  </>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}